/**
 * CAOS-507 — make a server-confirmed sign-out visible to every same-origin browser tab immediately.
 *
 * The session cookie is already shared. BroadcastChannel supplies the prompt cross-tab signal, while
 * the storage event is the compatibility path for browsers that cannot construct a channel. Both are
 * best-effort: the server remains the session authority and no client-side value is treated as auth.
 */

const SESSION_CHANNEL = 'caos-session';
export const SESSION_SIGNED_OUT_STORAGE_KEY = 'caos.session.signed-out';

interface SessionMessage {
  readonly type: 'signed-out';
}

function isSignedOutMessage(value: unknown): value is SessionMessage {
  return (
    value !== null &&
    typeof value === 'object' &&
    (value as { type?: unknown }).type === 'signed-out'
  );
}

export const SESSION_SIGN_OUT_ENDPOINT = '/api/auth/logout';

export interface SessionSignOutResponse {
  readonly ok?: boolean;
}

export type SessionSignOutRequest = () => Promise<SessionSignOutResponse>;

/**
 * Complete one host-side sign-out and announce it to sibling tabs. Any completed host response has
 * cleared the shared session cookie, even when the kernel returned an unsuccessful status. A
 * rejected request is not announced because the host may never have received it.
 */
export async function signOutSession(request: SessionSignOutRequest): Promise<void> {
  const response = await request();
  announceSessionSignedOut();
  if (!response.ok) throw new Error('The session could not be signed out.');
}

/** Announce a successful server-side sign-out to the other tabs on this origin. */
export function announceSessionSignedOut(): void {
  try {
    const channel = new BroadcastChannel(SESSION_CHANNEL);
    channel.postMessage({ type: 'signed-out' } satisfies SessionMessage);
    channel.close();
  } catch {
    /* BroadcastChannel may be unavailable or disabled; storage remains the fallback. */
  }

  try {
    // A distinct value is required: setting the same value does not raise a storage event elsewhere.
    window.localStorage.setItem(
      SESSION_SIGNED_OUT_STORAGE_KEY,
      `${Date.now()}:${Math.random()}`,
    );
  } catch {
    /* Storage may be unavailable; the server-side revocation still stands. */
  }
}

/**
 * Follow sign-out announcements from other tabs. At most one callback runs for this document even
 * when both transports deliver the same announcement.
 */
export function installSessionSignOutSync(onSignedOut: () => void): () => void {
  let handled = false;
  const accept = (): void => {
    if (handled) return;
    handled = true;
    onSignedOut();
  };

  const onStorage = (event: StorageEvent): void => {
    if (event.key === SESSION_SIGNED_OUT_STORAGE_KEY && event.newValue !== null) accept();
  };
  window.addEventListener('storage', onStorage);

  let channel: BroadcastChannel | undefined;
  let onMessage: ((event: MessageEvent<unknown>) => void) | undefined;
  try {
    channel = new BroadcastChannel(SESSION_CHANNEL);
    onMessage = (event): void => {
      if (isSignedOutMessage(event.data)) accept();
    };
    channel.addEventListener('message', onMessage);
  } catch {
    channel = undefined;
  }

  return () => {
    window.removeEventListener('storage', onStorage);
    if (channel && onMessage) channel.removeEventListener('message', onMessage);
    channel?.close();
  };
}
